/**
 * Game adapter for Hush lantern v2 (`src/shared/assets/hushLantern/`). The art is drawn in template-frame pixels,
 * so no projection is needed: the body, hang point, ribbon tie and window anchors are already frame-local.
 * Palette resolution and cache identity follow hushStatic (content digest of recipe + resolved roles + revision).
 */
import {
  HUSH_LANTERN_V2_HANGING, HUSH_LANTERN_V2_REVISION, HUSH_LANTERN_V2_STANDING, LANTERN_V2_FRAMES, buildLanternV2,
  type LanternV2Art, type LanternV2Recipe,
} from '../../../../../shared/assets/hushLantern';
import { HUSH_PROP_TEMPLATES } from '../../../../../shared/scene/hushPropTemplates';
import { TOWN_PROP_PALETTE_KEYS, type TownPropPalette } from '../../../../../shared/scene/townPropTemplates';
import { hushStaticRoles } from '../hushStatic';
import { sha256, stableStringify } from '../tidalCascade/rng';

export const HUSH_LANTERN_ADAPTER_REVISION = 'hush-lantern.adapter/revision-01';

export type HushLanternTemplate = 'three_window_lantern' | 'three_window_lantern_hanging';
export const HUSH_LANTERN_RECIPES: Readonly<Record<HushLanternTemplate, LanternV2Recipe>> = {
  three_window_lantern: HUSH_LANTERN_V2_STANDING,
  three_window_lantern_hanging: HUSH_LANTERN_V2_HANGING,
};

export function isHushLanternTemplate(t: unknown): t is HushLanternTemplate {
  return typeof t === 'string' && Object.prototype.hasOwnProperty.call(HUSH_LANTERN_RECIPES, t);
}

function check(template: unknown): asserts template is HushLanternTemplate {
  if (!isHushLanternTemplate(template)) throw new Error(`Unknown hush lantern template ${JSON.stringify(template)}`);
}

export interface HushLanternPlacement extends LanternV2Art {
  readonly template: HushLanternTemplate;
  readonly recipeId: string;
  readonly logicalRecipeHash: string;
  readonly collisionClaim: 'none';
}

const memo = new Map<string, HushLanternPlacement>();

export function describeHushLantern(template: HushLanternTemplate, palette: TownPropPalette): HushLanternPlacement {
  check(template);
  const recipe = HUSH_LANTERN_RECIPES[template];
  const roles = hushStaticRoles(recipe, palette);
  const logicalRecipeHash = sha256(stableStringify({ recipe, roles, revision: HUSH_LANTERN_V2_REVISION }));
  const key = template + logicalRecipeHash;
  const hit = memo.get(key);
  if (hit) return hit;
  const catalog = HUSH_PROP_TEMPLATES[template].frame;
  const frame = LANTERN_V2_FRAMES[recipe.params.support];
  if (catalog.w !== frame.w || catalog.h !== frame.h) throw new Error(`hush lantern ${template}: catalog frame drifted from the v2 builder`);
  const art = buildLanternV2(recipe, roles, `hl_${template}_${logicalRecipeHash.slice(0, 12)}`);
  const placed: HushLanternPlacement = { ...art, template, recipeId: recipe.id, logicalRecipeHash, collisionClaim: 'none' };
  if (memo.size >= 16) memo.delete(memo.keys().next().value as string);
  memo.set(key, placed);
  return placed;
}

export function buildHushLanternBody(template: HushLanternTemplate, palette: TownPropPalette): string {
  const placed = describeHushLantern(template, palette);
  return `<g data-part="hush-lantern-${template.replace(/_/g, '-')}" data-recipe="${placed.recipeId}" `
    + `data-integration="GENERATED_ASSET">${placed.body}</g>`;
}

/** `__tpl_<template>_<10 palette hexes>_<16-hex content digest>`. */
export function hushLanternTextureSuffix(template: HushLanternTemplate, palette: TownPropPalette): string {
  const { logicalRecipeHash, frame } = describeHushLantern(template, palette);
  const colors = TOWN_PROP_PALETTE_KEYS.map(k => palette[k].toString(16).padStart(6, '0')).join('-');
  const digest = sha256(stableStringify({ template, logicalRecipeHash, frame, adapter: HUSH_LANTERN_ADAPTER_REVISION })).slice(0, 16);
  return `__tpl_${template}_${colors}_${digest}`;
}
